<script lang="ts" setup>
import type { CalendarRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import type { DateValue } from '@internationalized/date'
import { CalendarDate } from '@internationalized/date'
import { computed, shallowRef, watch } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { CalendarRoot, useForwardPropsEmits } from 'reka-ui'
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeader,
  CalendarHeading,
  CalendarNextButton,
  CalendarPrevButton,
} from '.'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { cn } from '@/utils'

interface Props extends CalendarRootProps {
  class?: HTMLAttributes['class']
  /** When 'dropdown', replaces the prev/next heading with month and year selects */
  captionLayout?: 'buttons' | 'dropdown'
  hideClear?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  weekStartsOn: 1,
})
const emits = defineEmits<{
  'update:modelValue': [value: DateValue | undefined]
  'update:placeholder': [date: DateValue]
  'clear': []
}>()

const delegatedProps = reactiveOmit(props, 'class', 'captionLayout', 'hideClear')
const forwarded = useForwardPropsEmits(delegatedProps, emits)

// Track the displayed month/year for dropdown mode
const dropdownPlaceholder = shallowRef<DateValue>(
  (props.modelValue as CalendarDate | undefined)
  ?? (props.placeholder as CalendarDate | undefined)
  ?? new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, 1),
)

watch(
  () => props.modelValue,
  (val) => {
    if (val && props.captionLayout === 'dropdown') {
      dropdownPlaceholder.value = val as CalendarDate
    }
  },
)

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const yearRange = computed(() => {
  const minYear = props.minValue ? props.minValue.year : 1900
  const maxYear = props.maxValue ? props.maxValue.year : new Date().getFullYear()
  const years: number[] = []
  for (let y = minYear; y <= maxYear; y++) years.push(y)
  return years
})

function onMonthSelect(e: Event) {
  const month = Number((e.target as HTMLSelectElement).value)
  const cur = dropdownPlaceholder.value
  dropdownPlaceholder.value = new CalendarDate(cur.year, month, 1)
}

function onYearSelect(e: Event) {
  const year = Number((e.target as HTMLSelectElement).value)
  const cur = dropdownPlaceholder.value
  dropdownPlaceholder.value = new CalendarDate(year, cur.month, 1)
}
</script>

<template>
  <CalendarRoot
    v-slot="{ grid, weekDays }"
    :class="cn('p-3 items-stretch', props.class)"
    v-bind="forwarded"
    :placeholder="captionLayout === 'dropdown' ? dropdownPlaceholder : undefined"
    @update:placeholder="(d) => { if (captionLayout === 'dropdown') dropdownPlaceholder = d }"
  >
    <CalendarHeader>
      <template v-if="captionLayout === 'dropdown'">
        <div class="flex items-center gap-2">
          <!-- Year select -->
          <div class="relative h-6 w-20 flex flex-row justify-between rounded-md border border-surface-dark bg-surface-light pl-3 text-sm text-primary focus-within:outline-1 focus-within:outline-brand focus-within:outline cursor-pointer">
            <select
              id="calendar-year-select"
              :value="dropdownPlaceholder.year"
              class="appearance-none focus-visible:outline-0 focus:outline-0 outline-transparent font-bold text-xs bg-transparent h-full w-full pr-4"
              aria-label="Year"
              data-focus-first
              @change="onYearSelect"
            >
              <option
                v-for="year in yearRange"
                :key="year"
                :value="year"
              >
                {{ year }}
              </option>
            </select>
            <AM3Icon
              type="chevron-down"
              size="sm"
              class="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-secondary"
            />
          </div>

          <!-- Month select -->
          <div class="relative h-6 w-24 flex flex-row justify-between rounded-md border border-surface-dark bg-surface-light pl-3 text-sm text-primary focus-within:outline-1 focus-within:outline-brand focus-within:outline cursor-pointer">
            <select
              id="calendar-month-select"
              :value="dropdownPlaceholder.month"
              class="appearance-none focus-visible:outline-0 focus:outline-0 outline-transparent font-bold text-xs bg-transparent h-full w-full"
              aria-label="Month"
              @change="onMonthSelect"
            >
              <option
                v-for="(name, i) in monthNames"
                :key="i"
                :value="i + 1"
              >
                {{ name }}
              </option>
            </select>
            <AM3Icon
              type="chevron-down"
              size="sm"
              class="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-secondary"
            />
          </div>
        </div>

        <!-- Clear button -->
        <AM3Button
          v-if="!hideClear"
          type="tertiary"
          size="xs"
          text="Clear"
          @click="emits('clear')"
        />
      </template>
      <template v-else>
        <CalendarPrevButton />
        <CalendarHeading />
        <CalendarNextButton />
      </template>
    </CalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid
        v-for="month in grid"
        :key="month.value.toString()"
      >
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="day in weekDays"
              :key="day"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody>
          <CalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="mt-2 w-full"
          >
            <CalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
            >
              <CalendarCellTrigger
                :day="weekDate"
                :month="month.value"
              />
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  </CalendarRoot>
</template>
